import { useDisclosure } from "@chakra-ui/react";
import {
  OnDemandCumulativeReportType,
  SaasRoleName,
  SaasRoleNames,
} from "@intentsify/types";
import { useMutation } from "@tanstack/react-query";
import { cloneCampaign } from "api";
import {
  Actions,
  ActionsProps,
  RequestOnDemandCumulativeCampaignReport,
  RequestSpotlightReportModal,
  StatusModal,
} from "components";
import { RequestProgramReviewModal } from "components/RequestProgramReview/RequestProgramReviewModal";
import { useMemo, useState } from "react";
import { FiMoreVertical } from "react-icons/fi";
import { useNavigate } from "react-router";
import { CreateCampaignScreenDefinition } from "screens";
import { useUser } from "store/store.hooks";
import {
  UseCampaignActionItemsArg,
  useCampaignActionItems,
} from "./useCampaignActionItems";

export type ActionItemsProps = Omit<
  UseCampaignActionItemsArg,
  | "onClone"
  | "openRequestProgramReview"
  | "openRequestOnDemandCumulativeCampaignReportModal"
> &
  Pick<
    ActionsProps,
    "numberOfVisibleItems" | "buttonsVariant" | "iconsOnly"
  > & {
    campaignStartDate: string | undefined;
    campaignEndDate: string | undefined;
  };

const CampaignActionsItems = ({
  isDraft,
  campaignId,
  companyId,
  isLoading,
  numberOfVisibleItems,
  hasLeads,
  hasSpotlight,
  hasBeenMappedToConvertr,
  campaignCreatedDate,
  campaignStatusId,
  showCampaignDetailsAction,
  hasRelatedProgrammaticCampaigns,
  buttonsVariant = "ghost",
  iconsOnly = true,
  hasPixel,
  campaignStartDate,
  campaignEndDate,
}: ActionItemsProps) => {
  const user = useUser();
  const navigate = useNavigate();

  const { mutate: cloneMutation, isLoading: cloneLoading } = useMutation(
    () => cloneCampaign(campaignId),
    {
      onSuccess: (newCampaign) => {
        navigate(
          CreateCampaignScreenDefinition.navigate({
            campaignId: newCampaign.campaignId,
            step: 1,
          })
        );
      },
    }
  );

  const requestProgramReviewModalDisclosure = useDisclosure({});
  const requestOnDemandCumulativeCampaignReportModalDisclosure =
    useDisclosure();

  const [onDemandCumulativeReportType, setOnDemandCumulativeReportType] =
    useState<OnDemandCumulativeReportType | undefined>(undefined);

  const openRequestOnDemandCumulativeCampaignReportModal = (
    reportType: OnDemandCumulativeReportType
  ) => {
    setOnDemandCumulativeReportType(reportType);
    requestOnDemandCumulativeCampaignReportModalDisclosure.onOpen();
  };

  const items = useCampaignActionItems({
    isDraft,
    campaignId,
    companyId,
    isLoading,
    hasLeads,
    hasSpotlight,
    hasBeenMappedToConvertr,
    onClone: cloneMutation,
    campaignCreatedDate,
    campaignStatusId,
    showCampaignDetailsAction,
    hasRelatedProgrammaticCampaigns,
    openRequestProgramReview: requestProgramReviewModalDisclosure.onOpen,
    openRequestOnDemandCumulativeCampaignReportModal,
    hasPixel,
  });

  const numberOfVisibleActionItems = useMemo(() => {
    const isAdminOrPublisher = (
      [SaasRoleName.Admin, SaasRoleName.Publisher] as SaasRoleNames[]
    ).includes(user?.saasRole || SaasRoleName.Unspecified);

    if (isAdminOrPublisher) {
      return numberOfVisibleItems ?? 1;
    }

    return 1;
  }, [numberOfVisibleItems, user]);

  return (
    <>
      <Actions
        items={items}
        isLoading={isLoading}
        numberOfVisibleItems={numberOfVisibleActionItems}
        buttonIcon={<FiMoreVertical />}
        buttonsVariant={buttonsVariant}
        iconsOnly={iconsOnly}
      />
      <StatusModal isOpen={cloneLoading} status="Cloning campaign..." />
      <RequestSpotlightReportModal campaignId={campaignId} />
      <RequestProgramReviewModal
        campaignId={campaignId}
        isOpen={requestProgramReviewModalDisclosure.isOpen}
        onClose={requestProgramReviewModalDisclosure.onClose}
      />
      {onDemandCumulativeReportType && (
        <RequestOnDemandCumulativeCampaignReport
          campaignId={String(campaignId)}
          campaignStartDate={campaignStartDate}
          campaignEndDate={campaignEndDate}
          isOpen={requestOnDemandCumulativeCampaignReportModalDisclosure.isOpen}
          onClose={
            requestOnDemandCumulativeCampaignReportModalDisclosure.onClose
          }
          reportType={onDemandCumulativeReportType}
          firstImpressionDate={undefined}
        />
      )}
    </>
  );
};

export { CampaignActionsItems };
